import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { finalize, map } from 'rxjs/operators';

import { PhoneFormModel } from 'entity/forms/phone-form/phone-form.component';
import { digitsOnly, isNilOrEmpty } from 'helpers';

import {
  CreatePhoneGQL, CreatePhoneNumbersInput, FieldStatus, FieldStatusInput, UpdatePhoneGQL,
  UpdatePhoneNumberInput
} from 'generated/graphql';
import { invalidateApolloCacheFor } from 'core/apollo-cache-invalidator';
import { invalidateDuplicatesCache } from 'entity/helpers/invalidate-apollo-duplicates-cache';

/**
 * Format of data for the dialog
 */
export interface AddOrUpdatePhoneDialogData {
  entityId: string;
  campaignId: string;
  phone?: {
    id: string;
    number: string;
    /** Any type here due to casting issues
     * but should be PhoneNumberType enum */
    numberType: any;
    ext?: string;
    subscribed: boolean;
    status: FieldStatus;
  };
}

@Component({
  selector: 'app-add-or-update-phone-dialog',
  templateUrl: './add-or-update-phone.dialog.html'
})
export class AddOrUpdatePhoneDialog implements OnInit {
  isSaving = false;

  formControl = new UntypedFormControl();

  static open(dialog: MatDialog, config: MatDialogConfig<AddOrUpdatePhoneDialogData>) {
    return dialog.open(AddOrUpdatePhoneDialog, config);
  }

  constructor(
    private _createPhoneGQL: CreatePhoneGQL,
    private _updatePhoneGQL: UpdatePhoneGQL,
    public dialogRef: MatDialogRef<AddOrUpdatePhoneDialog>,
    @Inject(MAT_DIALOG_DATA) public data: AddOrUpdatePhoneDialogData
  ) {
  }

  /** If no phone data is provided, it is a create action */
  get isCreate() {
    return isNilOrEmpty(this.data?.phone);
  }

  ngOnInit(): void {
    if (isNilOrEmpty(this.data?.entityId) || isNilOrEmpty(this.data?.campaignId)) {
      throw new Error('An entityId and campaignId must be passed as data to `AddOrUpdatePhoneDialog`');
    }

    const initialFormData = this._translateToFormData(this.data);
    this.formControl.patchValue(
      initialFormData
    );
  }

  onSaveClicked() {
    this._save();
  }

  close(data: any = null) {
    this.dialogRef.close(data);
  }

  private _translateToFormData(dialogData: AddOrUpdatePhoneDialogData): PhoneFormModel {
    const phone = dialogData?.phone;
    if (!phone) {
      return null;
    }

    return {
      number: phone.number,
      ext: phone.ext,
      numberType: phone.numberType,
      verified: phone.status === FieldStatus.Verified,
      subscribed: phone.subscribed
    };
  }

  private _save() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    (this.isCreate ? this._createPhone() : this._updatePhone()).pipe(
      finalize(() => this.isSaving = false)
    ).subscribe((result) => {
      this.close(result);
    });
  }

  private _updatePhone() {
    const fv: PhoneFormModel = this.formControl.value;

    const input: UpdatePhoneNumberInput = {
      campaignId: this.data.campaignId,
      entityId: this.data.entityId,
      phoneNumberId: this.data.phone.id,
      changes: {
        number: fv.number,
        ext: fv.ext,
        numberType: fv.numberType,
        subscribe: fv.subscribed,
        status: fv.verified ? FieldStatusInput.Verified : FieldStatusInput.UserAdded
      }
    };

    return this._updatePhoneGQL.mutate(
      { input },
      {
        update: (cache) => invalidateDuplicatesCache(cache, this.data.entityId, this.data.campaignId)
      }
    ).pipe(
      map(res => res.data?.updatePhoneNumber.entity)
    );
  }

  private _createPhone() {
    const fv = this.formControl.value;

    const input: CreatePhoneNumbersInput = {
      campaignId: this.data.campaignId,
      entityId: this.data.entityId,
      phoneNumbers: [
        {
          number: fv.number,
          ext: fv.ext,
          numberType: fv.numberType,
          status: fv.verified ? FieldStatusInput.Verified : FieldStatusInput.UserAdded,
          subscribe: fv.subscribed
        }
      ]

    };

    return this._createPhoneGQL.mutate(
      { input },
      {
        update: (cache) => invalidateDuplicatesCache(cache, this.data.entityId, this.data.campaignId)
      }
      ).pipe(
      map(res => res.data?.createPhoneNumbers.entity)
    );
  }
}
